import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import AIChatPanel from "../../components/AIChatPanel";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  ShieldCheck,
  Gavel,
  ScrollText,
  TrendingUp,
  AlertTriangle,
  FileSearch,
} from "lucide-react";

const MODE_CHIPS = [
  { value: "rulebook", label: "Rulebook" },
  { value: "summary", label: "Debate Summary" },
  { value: "award_rationale", label: "Award Rationale" },
  { value: "participation", label: "Participation Insights" },
  { value: "resolution_review", label: "Resolution Review" },
];

const MODE_STARTERS = {
  rulebook: [
    "When can a delegate raise a Right of Reply?",
    "Explain the difference between moderated and unmoderated caucus.",
    "How does a chair entertain a motion to extend speakers' time?",
  ],
  summary: [
    "Summarise the last 30 minutes of debate in 5 bullets.",
    "List the major blocs that have emerged and what they want.",
    "What's the open question I should push delegates to address next?",
  ],
  award_rationale: [
    "Draft observations for the top 3 delegates this session.",
    "What 4 criteria should I weigh for Best Delegate?",
    "Suggest a fair criteria template for High Commendation.",
  ],
  participation: [
    "Which delegates have spoken less than 3 times today?",
    "What blocs haven't engaged yet and how do I activate them?",
    "Suggest 2 motions that would bring quieter delegates onto the speakers' list.",
  ],
  resolution_review: [
    "Review my draft resolution for preambulatory/operative balance.",
    "Are the operative clauses geopolitically coherent?",
    "Suggest 3 improvements before this is voted on.",
  ],
};

export default function ChairAssistantPage() {
  const [committee, setCommittee] = useState("");
  const [agenda, setAgenda] = useState("");
  const [context, setContext] = useState("");
  const [mode, setMode] = useState("rulebook");

  useEffect(() => {
    // Load chair's events to auto-fill committee/agenda
    api.get("/events").then((r) => {
      const ev = (r.data || [])[0];
      if (!ev) return;
      api.get(`/events/${ev.id}`).then((e) => {
        const c = (e.data?.councils || [])[0];
        if (c) {
          setCommittee(c.name);
          setAgenda(c.agenda || c.topic || "");
        }
      }).catch(() => {});
    }).catch(() => {});
  }, []);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto pb-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              AI Chair Assistant
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              The dais's <span className="font-serif italic text-[#0F4C81]">silent co-chair</span>.
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Rulebook lookups, debate summaries, participation insights and award-rationale support.
              Final decisions always remain with you.
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-900 text-xs">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span className="font-semibold">Human authority retained</span>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 space-y-5">
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                Committee context
              </p>
              <div className="mt-4 space-y-3" data-testid="chair-context-form">
                <div>
                  <Label className="text-xs">Committee</Label>
                  <Input value={committee} onChange={(e) => setCommittee(e.target.value)} placeholder="e.g. UNSC" data-testid="chair-committee" />
                </div>
                <div>
                  <Label className="text-xs">Agenda</Label>
                  <Input value={agenda} onChange={(e) => setAgenda(e.target.value)} placeholder="e.g. Maritime stability" data-testid="chair-agenda" />
                </div>
                <div>
                  <Label className="text-xs">Session context (optional)</Label>
                  <Textarea
                    rows={4}
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="e.g. Morning session covered SCS. P5 split. India proposed a 3-clause draft."
                    data-testid="chair-context"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-[#0A192F] text-white p-5 border border-[#D4AF37]/20">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                Quick reference
              </p>
              <div className="mt-3 space-y-2.5">
                <RefRow icon={Gavel} label="Rulebook" desc="Rule lookups & precedence" />
                <RefRow icon={ScrollText} label="Debate Summary" desc="Tight 4-6 bullet recap" />
                <RefRow icon={TrendingUp} label="Award Rationale" desc="Structured fairness notes" />
                <RefRow icon={FileSearch} label="Participation" desc="Under-spoken delegates & blocs" />
                <RefRow icon={AlertTriangle} label="Resolution Review" desc="Structural critique" />
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <AIChatPanel
              title="Chair Assistant"
              subtitle={committee && agenda ? `${committee} · ${agenda}` : "Set committee context on the left"}
              endpoint="/ai/chair-assist"
              fixedPayload={{ committee, agenda, context }}
              starters={MODE_STARTERS[mode]}
              api={api}
              modeChips={MODE_CHIPS}
              initialMode="rulebook"
              testidPrefix="chair-chat"
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function RefRow({ icon: Icon, label, desc }) {
  return (
    <div className="flex items-start gap-3 p-2.5 rounded-md bg-white/5">
      <div className="h-7 w-7 rounded-md bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
        <Icon className="h-3.5 w-3.5 text-[#D4AF37]" />
      </div>
      <div>
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-slate-400">{desc}</p>
      </div>
    </div>
  );
}
